import { getSiteApi } from '@/api-calls/sites/get'
import { Site } from '@/types/site'
import { useFetch } from '@ngtrvu/use-api'
import { createContext, ReactNode, useContext, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

// Config
export const SLUG_DOMAIN = import.meta.env.VITE_CMS_SLUG_DOMAIN || '.blgg.dev'

interface SiteContextValue {
  site: Site | undefined
  isLoading: boolean
  error: any
  siteId: string | undefined
  refresh: () => void
  sitePath: string | undefined
  siteDomain: string | undefined
  siteUrlWithScheme: string | undefined
  isDomainVerified: boolean
  userRole: 'admin' | 'editor' | 'author' | null
  isAdmin: boolean
  isEditor: boolean
  isAuthor: boolean
  canManageStaff: boolean
  canManageSettings: boolean
  canManageAllContent: boolean
}

const SiteContext = createContext<SiteContextValue | undefined>(undefined)

interface SiteProviderProps {
  children: ReactNode
}

export const getSiteDomain = (site: Site | undefined) => {
  if (!site) return undefined
  if (site.domainVerified && site.domain) {
    return site.domain
  }

  if (site.slug) {
    return `${site.slug}${SLUG_DOMAIN}`
  }

  return undefined
}

export const getSiteUrlWithScheme = (url: string | undefined) => {
  if (!url) return undefined
  const scheme = import.meta.env.PROD ? 'https' : 'http'
  return url ? `${scheme}://${url}` : undefined
}

const SiteProviderComponent = ({ children }: SiteProviderProps) => {
  const { siteId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const {
    data: site,
    isLoading,
    error,
    refresh,
  } = useFetch(getSiteApi, {
    resourceName: 'site',
    initialParams: {
      id: siteId as string,
    },
    autoLoad: true,
  })

  // Canonicalize the URL on the site slug: the API accepts either the site id
  // or the slug in the `:siteId` segment, so when the URL carries the id,
  // replace it with the slug in place.
  useEffect(() => {
    if (!site?.slug || !siteId || siteId === site.slug) return
    const rest = location.pathname.slice(`/${siteId}`.length)
    navigate(`/${site.slug}${rest}${location.search}`, { replace: true })
  }, [site?.slug, siteId, location.pathname, location.search, navigate])

  // Compute derived values
  const sitePath = site?.slug ? `/${site.slug}` : ''
  const isDomainVerified = Boolean(site?.domain)
  const siteDomain = getSiteDomain(site)
  const siteUrlWithScheme = getSiteUrlWithScheme(siteDomain)

  // Get user role from site data
  const userRole = site?.userRole || null

  // Compute role-based permissions
  const isAdmin = userRole === 'admin'
  const isEditor = userRole === 'editor'
  const isAuthor = userRole === 'author'
  const canManageStaff = isAdmin
  const canManageSettings = isAdmin
  const canManageAllContent = isAdmin || isEditor

  const value: SiteContextValue = {
    site,
    isLoading,
    error,
    sitePath,
    siteId: siteId as string,
    refresh,
    siteDomain,
    siteUrlWithScheme,
    isDomainVerified,
    userRole,
    isAdmin,
    isEditor,
    isAuthor,
    canManageStaff,
    canManageSettings,
    canManageAllContent,
  }

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>
}

export const SiteProvider = SiteProviderComponent

export function useSite(): SiteContextValue {
  const context = useContext(SiteContext)
  if (context === undefined) {
    throw new Error('useSite must be used within a SiteProvider')
  }
  return context
}
